/**
 * Recently viewed products utilities and section renderer.
 *
 * This module has two responsibilities:
 *
 * 1) ID-based tracking for predictive search empty state:
 *    - Exposed via the `RecentlyViewed` class.
 *    - Used by `predictive-search.js` and expects:
 *        RecentlyViewed.addProduct(productId)
 *        RecentlyViewed.getProducts() -> string[]
 *        RecentlyViewed.clearProducts()
 *    - Stores product IDs in localStorage under the `viewedProducts` key.
 *
 * 2) Handle-based tracking + rendering for the Recently Viewed section:
 *    - Exposed via the `RecentlyViewedHandles` class and the
 *      `<recently-viewed-products>` custom element.
 *    - Handles are stored separately under the `recently-viewed` key.
 */

/**
 * ID-based store for predictive search (legacy API).
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in localStorage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      window.localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      // Ignore storage errors.
    }
  }

  /**
   * Clears all recently viewed products.
   */
  static clearProducts() {
    try {
      window.localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }

  /**
   * Retrieves the list of recently viewed products from localStorage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      return JSON.parse(window.localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }
}

/**
 * Handle-based store for the Recently Viewed section.
 */
const HANDLE_STORAGE_KEY = 'recently-viewed';
const HANDLE_MAX_ITEMS = 4;

/**
 * Helper to read handles from localStorage as an array of unique strings.
 * @returns {string[]}
 */
function getStoredHandles() {
  try {
    const raw = window.localStorage.getItem(HANDLE_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((h) => typeof h === 'string' && h.trim().length > 0);
    }
    return [];
  } catch {
    return [];
  }
}

/**
 * Persist handles as a JSON array.
 * @param {string[]} handles
 */
function setStoredHandles(handles) {
  const unique = Array.from(new Set(handles)).filter((h) => h && h.trim().length > 0);
  const limited = unique.slice(0, HANDLE_MAX_ITEMS);
  try {
    window.localStorage.setItem(HANDLE_STORAGE_KEY, JSON.stringify(limited));
  } catch {
    // Ignore storage errors.
  }
}

export class RecentlyViewedHandles {
  /**
   * Add a product handle to the recently viewed handles list.
   * @param {string} handle
   */
  static add(handle) {
    if (typeof handle !== 'string' || !handle.trim()) return;
    const normalized = handle.trim();

    let handles = getStoredHandles().filter((h) => h !== normalized);
    handles.unshift(normalized);
    handles = handles.slice(0, HANDLE_MAX_ITEMS);
    setStoredHandles(handles);
  }

  /**
   * Get all stored handles (most recent first).
   * @returns {string[]}
   */
  static getHandles() {
    return getStoredHandles();
  }

  /**
   * Clear all stored handles.
   */
  static clear() {
    try {
      window.localStorage.removeItem(HANDLE_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }
}

/**
 * Custom element backing the Recently Viewed products section.
 */
class RecentlyViewedElement extends HTMLElement {
  connectedCallback() {
    // Load immediately when the element is connected.
    // The section itself will remain hidden when there is no data.
    this.#loadRecentlyViewed();
  }

  /**
   * Load and render recently viewed products into this section.
   */
  async #loadRecentlyViewed() {
    const sectionId = this.dataset.sectionId;
    const currentHandle = this.dataset.currentHandle;

    if (!sectionId) {
      console.error('recently-viewed-products: data-section-id is required on the element.');
      return;
    }

    const allHandles = RecentlyViewedHandles.getHandles();
    const handles = allHandles.filter((h) => h !== currentHandle);

    if (!handles.length) {
      this.classList.add('hidden');
      return;
    }

    const cards = await this.#fetchCardsForHandles(handles, sectionId);

    if (!cards.length) {
      this.classList.add('hidden');
      return;
    }

    this.#renderCards(cards);
  }

  /**
   * Fetch the section-rendered card HTML for each product handle.
   * @param {string[]} handles
   * @param {string} sectionId
   * @returns {Promise<string[]>}
   */
  async #fetchCardsForHandles(handles, sectionId) {
    const root = window.Shopify?.routes?.root || '/';
    const cards = [];

    // Fetch in parallel but handle failures per-item.
    await Promise.all(
      handles.map(async (handle) => {
        const url = `${root}products/${encodeURIComponent(
          handle
        )}?section_id=${encodeURIComponent(sectionId)}`;

        try {
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) {
            return;
          }

          const html = await response.text();
          const container = document.createElement('div');
          container.innerHTML = html;

          const card = container.querySelector('.recently-viewed-product-card');
          if (card && card.innerHTML.trim().length) {
            cards.push(card.innerHTML.trim());
          }
        } catch {
          // Ignore individual request failures.
        }
      })
    );

    return cards;
  }

  /**
   * Render the given card HTML snippets inside a resource-list wrapper
   * that matches the aeon-shopify theme conventions.
   * @param {string[]} cards
   */
  #renderCards(cards) {
    const layoutType = this.dataset.layoutType || 'grid';
    const columns = Number(this.dataset.columns || '4');
    const mobileColumns = Number(this.dataset.mobileColumns || '2');
    const columnsGap = Number(this.dataset.columnsGap || '16');
    const rowsGap = Number(this.dataset.rowsGap || '16');
    const carouselOnMobile = this.dataset.carouselOnMobile === 'true';

    // Render into the content area so the header/title stays intact.
    const content =
      this.querySelector('.section-resource-list__content') || this;

    // Remove any existing resource list or fallback card inside the content.
    content
      .querySelectorAll('.resource-list, .recently-viewed-product-card')
      .forEach((el) => el.remove());

    const wrapper = document.createElement('div');
    wrapper.className = [
      'resource-list',
      layoutType === 'carousel' ? 'resource-list__carousel' : 'resource-list--grid',
      layoutType === 'carousel' ? 'force-full-width' : '',
      carouselOnMobile && layoutType !== 'carousel' ? 'hidden--mobile' : '',
    ]
      .filter(Boolean)
      .join(' ');

    // Inline styles to mirror `resource-list` snippet CSS variables.
    const styles = [];
    if (layoutType === 'grid') {
      styles.push(`--resource-list-column-gap-desktop: ${columnsGap}px`);
      styles.push(`--resource-list-row-gap-desktop: ${rowsGap}px`);
      styles.push(`--resource-list-columns: repeat(${columns}, 1fr)`);
      styles.push(`--resource-list-columns-mobile: repeat(${mobileColumns}, 1fr)`);
      styles.push(`--column-count-mobile: ${mobileColumns}`);
    } else if (layoutType === 'carousel') {
      styles.push(`--resource-list-column-gap-desktop: ${columnsGap}px`);
      styles.push(`--column-count: ${columns}`);
      styles.push(`--column-count-mobile: 1`);
    }

    if (styles.length) {
      wrapper.style.cssText = styles.join('; ');
    }

    cards.forEach((html) => {
      const item = document.createElement('div');
      item.className = 'resource-list__item';
      item.innerHTML = html;
      wrapper.appendChild(item);
    });

    content.appendChild(wrapper);

    // Show section once content is ready.
    this.classList.remove('hidden');
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedElement);
}
